"use client";

import { util } from "@/common/theme";
import CustomSplitText from "@/mono/components/CustomSplitText";
import emitter from "@/mono/lib/emitter";
import { useScrollRigScrollTrigger } from "@/mono/lib/hooks/useScrollRigScrollTrigger";
import { pixelsToUnits } from "@/mono/lib/styles/WebGL";
import { setTypography } from "@/mono/lib/styles/mixins";
import { useGlobalStore } from "@/mono/store/global";

import { totalMeetImages } from "@/common/assets";
import useResize from "@/mono/lib/hooks/useResize";
import { lerp } from "@/mono/lib/math";
import { isBrowser } from "@/mono/lib/platform";
import { mediaMobile } from "@/mono/lib/responsive";
import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useThreeOutsideCanvasStore } from "../../app/ClientLayout";
import { ScrollingText } from "../ScrollingText";
import { SectionTitle } from "../SectionTitle";
import { MeetImage } from "./MeetImage";
import SplitText from "mono/components/SplitText";

export const MEET_IMAGE_START_TICKER = "MeetImage:startTicker";
export const MEET_IMAGE_STOP_TICKER = "MeetImage:stopTicker";
export const MEET_IMAGE_RESET = "MeetImage:reset";

const DEFAULT_DIMS = { width: 400, height: 600 };

export const NiceToMeetYou = (props) => {
  const wrapper = useRef();
  const overlay = useRef();
  const bounds = useRef();
  const xBoundary = useRef(0);
  const xOffset = useRef(0);
  const xViewportHalf = useRef(0);

  const camera = useThreeOutsideCanvasStore((s) => s.camera);
  const windowSize = useGlobalStore((s) => s.windowSize);

  const [imageDims, setImageDims] = useState(() =>
    Array.from({ length: totalMeetImages }, () => ({ ...DEFAULT_DIMS }))
  );

  useEffect(() => {
    if (!isBrowser()) return;
    let cancelled = false;
    Promise.all(
      Array.from(
        { length: totalMeetImages },
        (_, i) =>
          new Promise((resolve) => {
            const img = new Image();
            img.onload = () =>
              resolve({
                width: img.naturalWidth || DEFAULT_DIMS.width,
                height: img.naturalHeight || DEFAULT_DIMS.height,
              });
            img.onerror = () => resolve({ ...DEFAULT_DIMS });
            img.src = `/images/meet-us/${i + 1}.webp`;
          })
      )
    ).then((dims) => {
      if (!cancelled) setImageDims(dims);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const meetUsImages = imageDims;

  const isMobileLayout = useGlobalStore((s) => s.isMobileLayout);

  const imageOverlayStyle = useMemo(() => {
    return Object.assign(
      {},
      ...meetUsImages.map((img, i) => {
        const baseWidth = 400;
        const baseMargin = 300;
        let scale = (baseWidth / img.width);

        if (img.width > img.height) {
          scale *= 2;
        }

        const r = seededRandom(i * 3) * 200;
        let randomOffset =
          lerp(r, r * (Math.sin(i) * 0.5), seededRandom(i * 3 + 1) * 0.8) - 100;
        let randomMargin = baseMargin + seededRandom(i * 3 + 2) * 100;

        if (isMobileLayout) {
          scale *= 0.8;
          randomOffset *= 0.9;
        }

        return {
          [`--image-${i + 1}-width`]: `${Math.round(img.width * scale)}rem`,
          [`--image-${i + 1}-height`]: `${Math.round(img.height * scale)}rem`,
          [`--image-${i + 1}-offset-y`]: `${Math.round(randomOffset)}rem`,
          [`--image-${i + 1}-margin`]: `${Math.round(randomMargin)}rem`,
        };
      })
    );
  }, [meetUsImages, isMobileLayout]);

  useResize(
    () => {
      const liveCamera = useThreeOutsideCanvasStore.getState().camera;
      const liveWindowSize = useGlobalStore.getState().windowSize;
      if (!liveCamera || !overlay.current) return;

      bounds.current = overlay.current.getBoundingClientRect();

      let totalWidthPx = 0;
      const children = overlay.current.querySelectorAll(".meet-image");
      children.forEach((child) => {
        const rect = child.getBoundingClientRect();
        const marginRight = parseFloat(
          getComputedStyle(child).marginRight || "0"
        );
        totalWidthPx += rect.width + (Number.isFinite(marginRight) ? marginRight : 0);
      });

      if (totalWidthPx <= 0) return;

      xBoundary.current = pixelsToUnits(
        totalWidthPx,
        liveCamera,
        liveWindowSize.height
      );

      xViewportHalf.current = pixelsToUnits(
        liveWindowSize.width / 2,
        liveCamera,
        liveWindowSize.height
      );

      xOffset.current = 0;

      emitter.emit(MEET_IMAGE_RESET);
    },
    {
      immediate: true,
      debounce: 150,
    },
    [overlay.current, camera, meetUsImages, windowSize, imageDims]
  );

  function getBoundsX() {
    return xBoundary.current;
  }

  function getOffsetX() {
    return xOffset.current;
  }

  function getViewportHalfX() {
    return xViewportHalf.current;
  }

  useScrollRigScrollTrigger({
    el: wrapper,

    handleIn: async () => {
      emitter.emit(MEET_IMAGE_START_TICKER);
    },
    handleOut: () => {
      emitter.emit(MEET_IMAGE_STOP_TICKER);
    },
  });

  return (
    <>
      <SectionTitle>Nice to meet you</SectionTitle>

      <Wrapper ref={wrapper}>
        <ScrollingText>Dream big and make it real</ScrollingText>

        <CenterText>
          <SplitText animateOnScroll={true} tag="h1">
            Come say hi!
          </SplitText>
          <SplitText animateOnScroll={true} tag="h3">
            Lets meet <b>at the bar?</b>
          </SplitText>
        </CenterText>

        <div
          className="nice-to-meet-you__spacer"
          style={{
            height: "400rem",
            width: "100%",
          }}
        ></div>

        <ImageOverlay ref={overlay} $images={meetUsImages} style={imageOverlayStyle}>
          {meetUsImages.map((_, i) => {
            return (
              <MeetImage
                key={i}
                i={i}
                getBoundsX={getBoundsX}
                getOffsetX={getOffsetX}
                getViewportHalfX={getViewportHalfX}
                src={`/images/meet-us/${i + 1}.webp`}
                className={`meet-image image-overlay-${i + 1}`}
              />
            );
          })}
        </ImageOverlay>
      </Wrapper>
    </>
  );
};

const Wrapper = styled.div`
  ${util.inner};

  .scrolling-text {
    max-width: 1200rem;
  }

  position: relative;
  overflow: visible;

  margin-bottom: 500rem;
`;

function seededRandom(seed) {
  const x = Math.sin(seed + 1) * 10000;
  return x - Math.floor(x);
}

const ImageOverlay = styled.div`
  top: 80%;
  transform: translate(0%, -50%);
  position: absolute;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  overflow: visible;

  .meet-image {
    --transformMultiplier: 4;
    visibility: hidden;
    flex-shrink: 0;

    ${mediaMobile(`
      margin-right: 100rem;
    `)}

    transform: translate(
      0px,
      calc(var(--offset-y) * var(--transformMultiplier))
    );
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${({ $images }) =>
    $images
      .map(
        (_, i) => `
    .image-overlay-${i + 1} {
      width: var(--image-${i + 1}-width);
      height: var(--image-${i + 1}-height);
      --offset-y: var(--image-${i + 1}-offset-y);
      margin-right: var(--image-${i + 1}-margin);
    }
  `
      )
      .join("\n")}
`;

const CenterText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  position: absolute;
  right: 20%;
  top: 40%;

  h1 {
    color: ${({ theme }) => theme.theme.floatingTitle};
    ${setTypography("floatingTitle")}
  }

  h3 {
    color: ${({ theme }) => theme.theme.floatingTitle};
  }

  ${mediaMobile(`
    top: 50%;
  `)}
`;
